<script lang="ts" setup>
import {
  type IImpactNumber,
  MainHeader,
  formatCountSuffix
} from "@frontiers-vue3/public-pages";
import { RTImpactLinkTitleEnum } from "~/enums";
import type { IMainHeader } from "./IMainHeader";
import type { IRTHeaderImpactLink } from "./components/RTHeaderImpactLink/IRTHeaderImpactLink";
import RTHeaderImpactLink from "./components/RTHeaderImpactLink/RTHeaderImpactLink.vue";
import DialogSubmitManuscript from "~/components/common/components/DialogSubmitManuscript/DialogSubmitManuscript.vue";

const props = defineProps<IMainHeader>();

const toggleSubmitManuscript = ref(false);
provide("toggleSubmitManuscript", toggleSubmitManuscript);

const terminology = useTerminologyStore();
const {
  currentResearchTopic,
  getParticipatingJournalsToSubmit,
  breadCrumbItems
} = useMain();
const { rtId } = useRoute().params;
const {
  isOpenForColleagueReferral,
  isParticipateReferColleagueButtonVisible,
  baseUrl
} = useRuntimeConfig().public;

const { isImpactMetricsVisible, minimumRtDownloadsCount } =
  useEnvironmentConfigStore().getConfig();

const mainHeaderCustomLinks = computed((): IRTHeaderImpactLink[] => {
  const { closedOn, availableEbooks, isOpenForManuscriptSubmission } =
    currentResearchTopic;

  let links: IRTHeaderImpactLink[] = [];

  const submitManuscript: IRTHeaderImpactLink = {
    linkToRedirect: `button`,
    title: RTImpactLinkTitleEnum.SUBMIT_MANUSCRIPT,
    iconTitle: "arrowRight"
  };

  if (closedOn && !isOpenForManuscriptSubmission) {
    const buttonsForClosedRt: IRTHeaderImpactLink[] = [];
    availableEbooks.includes("PDF") &&
      buttonsForClosedRt.push({
        linkToRedirect: `/research-topics/${rtId}/pdf`,
        title: RTImpactLinkTitleEnum.DOWNLOAD_PDF,
        iconTitle: "arrowRight",
        target: "_blank"
      });
    availableEbooks.includes("EPUB") &&
      buttonsForClosedRt.push({
        linkToRedirect: `/research-topics/${rtId}/epub`,
        title: RTImpactLinkTitleEnum.DOWNLOAD_EPUB,
        iconTitle: "arrowRight",
        target: "_blank"
      });
    links = buttonsForClosedRt;
  }

  const submissionDeadline = new Date(
    currentResearchTopic.manuscriptSubmissionDeadline
  );
  if (
    currentResearchTopic.isOpenForManuscriptSubmission &&
    !currentResearchTopic.closedOn &&
    isOpenForColleagueReferral &&
    isParticipateReferColleagueButtonVisible &&
    submissionDeadline >= new Date(new Date().toISOString())
  ) {
    links = [
      ...links,
      {
        linkToRedirect: `${baseUrl}/research-topics/${rtId}/${currentResearchTopic.slug}/participate-in-open-access-research-topic`,
        title: `${RTImpactLinkTitleEnum.PARTICIPATE} ${terminology
          .getTerm("topicTitle")
          .toLowerCase()}` as RTImpactLinkTitleEnum.PARTICIPATE
      }
    ];
  }

  if (props.isSubmitManuscriptEnabled) links.push(submitManuscript);

  const { isManageTopicWheelAvailable } = useAuthStore();
  if (isManageTopicWheelAvailable) {
    links = [
      ...links,
      {
        linkToRedirect: "",
        title: RTImpactLinkTitleEnum.EMPTY,
        iconTitle: "settingsIcon"
      }
    ];
  }

  return links;
});

const impactDownloads = computed((): number => {
  const { downloads } = currentResearchTopic.impactV5 || { downloads: 0 };

  if (isImpactMetricsVisible && downloads > minimumRtDownloadsCount)
    return downloads;
  return 0;
});

const impactViewsAndDownloads = computed((): number => {
  const { views } = currentResearchTopic.impactV5 || { views: 0 };

  if (isImpactMetricsVisible && views > minimumRtDownloadsCount) return views;

  return 0;
});

const impactNumbers = computed((): IImpactNumber[] => {
  const impactNumber = [];
  if (impactDownloads.value) {
    const { value, suffix } = formatCountSuffix(impactDownloads.value);
    impactNumber.push({
      number: value ? value : 0,
      text: "Total downloads",
      suffix
    });
  }

  if (impactViewsAndDownloads.value) {
    const { value, suffix } = formatCountSuffix(impactViewsAndDownloads.value);
    impactNumber.push({
      number: value ? value : 0,
      text: "Total views and downloads",
      suffix
    });
  }

  return impactNumber;
});
</script>
<template>
  <MainHeader
    heading="h1"
    :title="currentResearchTopic.title"
    :title-allowed-tags="['sub', 'sup']"
    :impact-numbers="impactNumbers"
    :breadcrumbs="breadCrumbItems">
    <template #buttons>
      <RTHeaderImpactLink
        v-for="(impact, index) in mainHeaderCustomLinks"
        :key="index"
        v-bind="impact"
        class="MainHeader__link" />
    </template>
  </MainHeader>

  <DialogSubmitManuscript
    v-if="isSubmitManuscriptEnabled"
    :participating-journals-to-submit="getParticipatingJournalsToSubmit"
    :is-dialog-open="toggleSubmitManuscript" />
</template>
<style lang="scss">
.MainHeader {
  .MainHeader__link {
    &::after {
      content: none;
    }
  }

  .MainHeader__buttons {
    gap: 2px;
    flex-wrap: nowrap;

    .MainHeader__link {
      border-left: 0;
    }

    @media #{$break-xs} {
      border-left: 0;
    }
  }

  &__title {
    sub,
    sup {
      all: revert;
      font-size: 75%;
    }
  }
}
</style>
